import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "./api";
import {
  AlertIcon,
  AuditIcon,
  CheckIcon,
  ChevronIcon,
  EvidenceIcon,
  GridIcon,
  LinkIcon,
  PlayIcon,
  PolicyIcon,
  RefreshIcon,
  SparkIcon,
  TaskIcon,
} from "./icons";
import type {
  AuditEvent,
  DashboardResponse,
  EvidenceView,
  HealthStatus,
  ImpactAsset,
  PolicyView,
  ProofObligation,
  Severity,
  TaskDetail,
  TaskStatus,
  TaskSummary,
} from "./types";

type Page = "command" | "workspace" | "evidence" | "audit" | "policies";

const STATUS_LABEL: Record<TaskStatus, string> = {
  assessing: "Assessing",
  needs_review: "Needs review",
  changes_requested: "Changes requested",
  authorized: "Authorized",
  executing: "Executing",
  verifying: "Verifying",
  completed: "Completed",
  blocked: "Blocked",
  superseded: "Superseded",
  cancelled: "Cancelled",
};

const ACTIVE_STATES: TaskStatus[] = ["assessing", "executing", "verifying"];

function formatTime(value?: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat("en", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(date);
}

function humanize(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function coveragePercent(covered = 0, total = 0) {
  return total ? Math.round((covered / total) * 100) : 0;
}

function modelLabel(health: HealthStatus | null) {
  if (!health || health.agent_framework === "deterministic") return "Deterministic local demo";
  if (health.agent_framework === "strands_agents") {
    const runtime = health.cloud_runtime === "agentcore_runtime" ? "AgentCore" : health.cloud_runtime === "aws_lambda" ? "AWS Lambda" : "local";
    return `${health.model_id} · Strands Agents · ${runtime}`;
  }
  return `${health.model_id.replace("gemini-", "Gemini ").replace("-flash", " Flash")} · Google ADK · ${health.cloud_runtime === "cloud_run" ? "Cloud Run" : "local"}`;
}

function proposalSourceLabel(source: TaskDetail["proposal"]["source"]) {
  if (source === "strands_deepseek") return "DeepSeek via Strands Agents";
  if (source === "strands_bedrock") return "Amazon Bedrock via Strands Agents";
  if (source === "google_adk") return "Gemini via Google ADK";
  return "Deterministic fallback";
}

function StatusBadge({ status }: { status: TaskStatus }) {
  return <span className={`status-badge status-${status}`}><i />{STATUS_LABEL[status]}</span>;
}

function SeverityBadge({ severity }: { severity: Severity }) {
  return <span className={`severity severity-${severity}`}>{severity}</span>;
}

function EmptyState({ title, detail }: { title: string; detail: string }) {
  return <div className="empty-state"><span className="empty-ring"><CheckIcon /></span><h3>{title}</h3><p>{detail}</p></div>;
}

function LoadingBlock({ lines = 3 }: { lines?: number }) {
  return <div className="loading-block" aria-label="Loading">{Array.from({ length: lines }, (_, index) => <i key={index} />)}</div>;
}

function App() {
  const [page, setPage] = useState<Page>("command");
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [evidence, setEvidence] = useState<EvidenceView[]>([]);
  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const [policies, setPolicies] = useState<PolicyView[]>([]);
  const [loading, setLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewReason, setReviewReason] = useState("Scope, target revision, and execution boundary reviewed.");

  const showError = useCallback((caught: unknown) => {
    const message = caught instanceof ApiError ? caught.message : caught instanceof Error ? caught.message : "The request could not be completed.";
    setError(message);
  }, []);

  const loadDashboard = useCallback(async () => {
    const data = await api.dashboard();
    setDashboard(data);
    if (!taskId && data.priority_tasks.length) setTaskId(data.priority_tasks[0].id);
    return data;
  }, [taskId]);

  const loadTask = useCallback(async (id = taskId) => {
    if (!id) return null;
    const data = await api.task(id);
    setTask(data);
    setTaskId(data.id);
    return data;
  }, [taskId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([api.health().then(setHealth), loadDashboard(), taskId ? loadTask(taskId) : Promise.resolve(null)])
      .catch((caught) => active && showError(caught))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
    // Initial hydration is intentionally performed once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!taskId || task?.id === taskId) return;
    setSectionLoading(true);
    loadTask(taskId).catch(showError).finally(() => setSectionLoading(false));
  }, [loadTask, showError, task?.id, taskId]);

  useEffect(() => {
    if (!task || !ACTIVE_STATES.includes(task.status)) return;
    const timer = window.setInterval(() => {
      loadTask(task.id).then((updated) => {
        if (updated && !ACTIVE_STATES.includes(updated.status)) {
          loadDashboard().catch(showError);
        }
      }).catch(showError);
    }, 1200);
    return () => window.clearInterval(timer);
  }, [loadDashboard, loadTask, showError, task]);

  useEffect(() => {
    if (page === "evidence" && taskId) {
      setSectionLoading(true);
      api.evidence(taskId).then(setEvidence).catch(showError).finally(() => setSectionLoading(false));
    }
    if (page === "audit" && taskId) {
      setSectionLoading(true);
      api.audit(taskId).then(setAudit).catch(showError).finally(() => setSectionLoading(false));
    }
    if (page === "policies") {
      setSectionLoading(true);
      api.policies().then(setPolicies).catch(showError).finally(() => setSectionLoading(false));
    }
  }, [page, showError, taskId]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const navigate = (next: Page, selectedId?: string) => {
    if (selectedId) setTaskId(selectedId);
    setError(null);
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const refresh = async () => {
    setSectionLoading(true);
    setError(null);
    try {
      await Promise.all([api.health().then(setHealth), loadDashboard(), taskId ? loadTask(taskId) : Promise.resolve(null)]);
      setToast("Latest service state loaded");
    } catch (caught) { showError(caught); }
    finally { setSectionLoading(false); }
  };

  const reset = async () => {
    setActionLoading("reset"); setError(null);
    try {
      const result = await api.reset();
      setTaskId(result.task_id);
      await Promise.all([loadTask(result.task_id), loadDashboard()]);
      setPage("workspace");
      setToast("Demo task reset from the service");
    } catch (caught) { showError(caught); }
    finally { setActionLoading(null); }
  };

  const submitReview = async (verdict: "approve" | "request_changes") => {
    if (!task || reviewReason.trim().length < 3) return;
    setActionLoading(verdict); setError(null);
    try {
      const updated = await api.review(task, verdict, reviewReason.trim());
      setTask(updated);
      setReviewOpen(false);
      await loadDashboard();
      setToast(verdict === "approve" ? "Approval recorded — release remains evidence-gated" : "Changes requested and recorded");
    } catch (caught) { showError(caught); }
    finally { setActionLoading(null); }
  };

  const execute = async () => {
    if (!task) return;
    setActionLoading("execute"); setError(null);
    try {
      await api.execute(task);
      await loadTask(task.id);
      setToast("Approved action started");
    } catch (caught) { showError(caught); }
    finally { setActionLoading(null); }
  };

  const nav = [
    { id: "command" as const, label: "Command Center", icon: GridIcon },
    { id: "workspace" as const, label: "Task Workspace", icon: TaskIcon },
    { id: "evidence" as const, label: "Evidence", icon: EvidenceIcon },
    { id: "audit" as const, label: "Audit", icon: AuditIcon },
    { id: "policies" as const, label: "Policies", icon: PolicyIcon },
  ];

  return <div className="app-shell">
    <header className="topbar">
      <button className="brand" onClick={() => navigate("command")} aria-label="Open Command Center">
        <span className="brand-symbol"><i /><i /><i /></span>
        <span><strong>AssureOps</strong><small>Sentinel</small></span>
      </button>
      <div className="topbar-context">
        <span className="connection"><i />{modelLabel(health)}</span>
        <span className="mode">Synthetic evidence · v{health?.version || "—"}</span>
        <button className="refresh-button" onClick={refresh} disabled={sectionLoading} aria-label="Refresh service state"><RefreshIcon /></button>
        <span className="avatar">DO</span>
      </div>
    </header>
    <aside className="sidebar">
      <nav aria-label="Primary navigation">
        <p className="nav-label">Operations</p>
        {nav.map(({ id, label, icon: NavIcon }) => <button key={id} className={`nav-link ${page === id ? "active" : ""}`} onClick={() => navigate(id)} disabled={id !== "command" && id !== "policies" && !taskId}>
          <NavIcon /><span>{label}</span>{id === "workspace" && dashboard?.counters.needs_review ? <b>{dashboard.counters.needs_review}</b> : null}
        </button>)}
      </nav>
      <div className="trust-card"><span><PolicyIcon />Trust boundary</span><p>Agents propose. Humans authorize. Evidence decides.</p></div>
      <button className="reset-button" onClick={reset} disabled={actionLoading === "reset"}><RefreshIcon />{actionLoading === "reset" ? "Resetting…" : "Reset demo"}</button>
    </aside>
    <main className="main-content">
      {actionLoading === "reset" && <div className="model-progress" role="status"><span className="spinner" /><div><strong>{health?.agent_framework === "strands_agents" ? "Strands is deriving a bounded proof plan" : health?.agent_framework === "google_adk" ? "Gemini is deriving a bounded proof plan" : "Building the deterministic proof plan"}</strong><span>The agent may propose checks, but it cannot create evidence, approve execution, or decide release readiness.</span></div></div>}
      {error && <div className="error-banner" role="alert"><AlertIcon /><div><strong>Service request failed</strong><span>{error}</span></div><button onClick={() => setError(null)}>Dismiss</button></div>}
      {loading ? <PageSkeleton /> : page === "command" ? <CommandCenter dashboard={dashboard} health={health} onOpen={(id) => navigate("workspace", id)} />
        : page === "workspace" ? <TaskWorkspace task={task} loading={sectionLoading} onReview={() => setReviewOpen(true)} onExecute={execute} actionLoading={actionLoading} />
        : page === "evidence" ? <EvidencePage task={task} items={evidence} loading={sectionLoading} />
        : page === "audit" ? <AuditPage task={task} items={audit} loading={sectionLoading} />
        : <PoliciesPage items={policies} loading={sectionLoading} />}
    </main>
    {reviewOpen && task && <ReviewDialog task={task} reason={reviewReason} loading={actionLoading} onReason={setReviewReason} onClose={() => setReviewOpen(false)} onSubmit={submitReview} />}
    {toast && <div className="toast" role="status"><CheckIcon />{toast}</div>}
  </div>;
}

function PageSkeleton() {
  return <div><div className="page-title skeleton-title"/><div className="metric-grid"><LoadingBlock/><LoadingBlock/><LoadingBlock/><LoadingBlock/></div><div className="panel"><LoadingBlock lines={7}/></div></div>;
}

function PageHeader({ eyebrow, title, description, aside }: { eyebrow: string; title: string; description: string; aside?: React.ReactNode }) {
  return <header className="page-header"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{description}</p></div>{aside}</header>;
}

function CommandCenter({ dashboard, health, onOpen }: { dashboard: DashboardResponse | null; health: HealthStatus | null; onOpen: (id: string) => void }) {
  if (!dashboard) return <EmptyState title="Command Center unavailable" detail="Connect the AssureOps API to load operational tasks." />;
  const metrics = [
    ["Open tasks", dashboard.counters.open, "Across monitored changes", "violet"],
    ["Needs approval", dashboard.counters.needs_review, "Awaiting human decision", "amber"],
    ["Running now", dashboard.counters.running, "Approved operations", "blue"],
    ["Verified today", dashboard.counters.verified, "Evidence-complete tasks", "green"],
  ] as const;
  return <>
    <PageHeader eyebrow="Operations overview" title="Command Center" description="Prioritize decisions, follow controlled execution, and close work with evidence." aside={<div className="live-chip"><i />Live service state</div>} />
    <section className="demo-story" aria-label="Demo scenario">
      <div><p className="eyebrow">Sentinel demo · NimbusCart</p><h2>One breaking API change. Four impacted systems. Zero trusted evidence.</h2><p>AssureOps turns the gap into one governed, reviewable action—then lets verified evidence, not the model, decide readiness.</p></div>
      <div className="story-path" aria-label="Assurance path"><span>OpenAPI change</span><i>→</i><span>Impact graph</span><i>→</i><span>Proof obligations</span><i>→</i><span>Human approval</span><i>→</i><span>JUnit evidence</span></div>
    </section>
    <RuntimeProof health={health} />
    <section className="metric-grid" aria-label="Task metrics">{metrics.map(([label, value, detail, color]) => <article className={`metric-card metric-${color}`} key={label}><span>{label}</span><strong>{value}</strong><small>{detail}</small><i /></article>)}</section>
    <div className="command-grid">
      <section className="panel queue-panel">
        <div className="panel-heading"><div><p className="eyebrow">Decision queue</p><h2>Needs your attention</h2></div><span className="panel-count">{dashboard.priority_tasks.length} prioritized</span></div>
        {dashboard.priority_tasks.length ? <div className="task-list">{dashboard.priority_tasks.map((item) => <TaskRow key={item.id} task={item} onOpen={onOpen} />)}</div> : <EmptyState title="Queue is clear" detail="There are no prioritized assurance tasks." />}
      </section>
      <section className="panel activity-panel">
        <div className="panel-heading"><div><p className="eyebrow">Append-only record</p><h2>Recent activity</h2></div></div>
        {dashboard.recent_activity.length ? <div className="activity-list">{dashboard.recent_activity.slice(0, 6).map((event) => <ActivityItem event={event} key={`${event.sequence}-${event.occurred_at}`} />)}</div> : <EmptyState title="No activity yet" detail="Task events will appear here." />}
      </section>
    </div>
  </>;
}

function RuntimeProof({ health }: { health: HealthStatus | null }) {
  const values = health?.agent_framework === "strands_agents" ? [
    ["Model", health.model_id, health.model_backend === "amazon_bedrock" ? "via Amazon Bedrock" : "via DeepSeek API"],
    ["Agent", "Strands Agents", "Proposal-only authority"],
    ["Runtime", health.cloud_runtime === "agentcore_runtime" ? "Amazon Bedrock AgentCore" : health.cloud_runtime === "aws_lambda" ? "AWS Lambda" : "Local process", health.model_location || "Bounded invocation"],
    ["Decision", "Evidence policy", "Deterministic + fail-closed"],
  ] : health?.agent_framework === "google_adk" ? [
    ["Model", "Gemini 3.5 Flash", health.model_backend === "vertex_ai" ? "via Vertex AI" : "via Gemini API"],
    ["Agent", "Google ADK", "Proposal-only authority"],
    ["Runtime", health.cloud_runtime === "cloud_run" ? "Google Cloud Run" : "Local process", health.cloud_revision || "Authenticated deployment"],
    ["Decision", "Evidence policy", "Deterministic + fail-closed"],
  ] : [
    ["Model", "Deterministic", "Offline development adapter"],
    ["Agent", "Typed runner", "Same proposal contract"],
    ["Runtime", "Local process", "Synthetic demo data"],
    ["Decision", "Evidence policy", "Deterministic + fail-closed"],
  ];
  return <section className="runtime-proof" aria-label="Verified runtime architecture">{values.map(([label, value, detail]) => <article key={label}><span>{label}</span><strong>{value}</strong><small title={detail}>{detail}</small></article>)}</section>;
}

function TaskRow({ task, onOpen }: { task: TaskSummary; onOpen: (id: string) => void }) {
  const percent = coveragePercent(task.coverage.satisfied, task.coverage.total);
  return <button className="task-row" onClick={() => onOpen(task.id)}>
    <span className={`task-mark severity-mark-${task.severity}`}><TaskIcon /></span>
    <span className="task-copy"><strong>{task.title}</strong><small>{humanize(task.kind)} · {task.environment} · revision {task.target_revision}</small></span>
    <span className="task-owner"><small>Target</small><b>{task.target_revision}</b></span>
    <span className="task-coverage"><small>{percent}% evidence</small><i><b style={{ width: `${percent}%` }} /></i></span>
    <span className="task-state"><StatusBadge status={task.status} /><small>{task.gap_count} open gaps</small></span>
    <ChevronIcon className="chevron" />
  </button>;
}

function ActivityItem({ event }: { event: AuditEvent }) {
  return <article className="activity-item"><span className="activity-dot"><CheckIcon /></span><div><strong>{event.summary}</strong><p>{humanize(event.event_type)}</p><small>{event.actor} · {formatTime(event.occurred_at)}</small></div></article>;
}

function TaskWorkspace({ task, loading, onReview, onExecute, actionLoading }: { task: TaskDetail | null; loading: boolean; onReview: () => void; onExecute: () => void; actionLoading: string | null }) {
  if (!task) return <EmptyState title="No task selected" detail="Choose a task from Command Center to open its assurance workspace." />;
  const percent = coveragePercent(task.coverage.satisfied, task.coverage.total);
  const assets: ImpactAsset[] = task.impact;
  const canReview = task.allowed_actions.some((item) => ["review", "approve", "request_changes"].includes(item));
  const canExecute = task.allowed_actions.includes("execute");
  const busy = ["assessing", "executing", "verifying"].includes(task.status);
  return <>
    <PageHeader eyebrow={`${humanize(task.kind)} · ${task.environment}`} title={task.title} description={`Target revision ${task.target_revision} · server revision ${task.revision}`} aside={<div className="header-status"><StatusBadge status={task.status} /><span className={`release-${task.release_decision}`}>{task.release_decision === "ready" ? <CheckIcon /> : <AlertIcon />}{humanize(task.release_decision)}</span></div>} />
    <div className="workflow-rail" aria-label="Task workflow">
      {[["Context", true], ["Plan", task.status !== "assessing"], ["Review", !["assessing", "needs_review"].includes(task.status)], ["Execute", ["executing", "verifying", "completed"].includes(task.status)], ["Verified", task.status === "completed" && task.release_decision === "ready"]].map(([label, done], index) => <div className={done ? "complete" : index === 2 && task.status === "needs_review" ? "current" : ""} key={String(label)}><span>{done ? <CheckIcon /> : index + 1}</span><b>{label}</b></div>)}
    </div>
    {busy && <div className="running-banner"><span className="spinner" /><div><strong>{STATUS_LABEL[task.status]}</strong><p>Task state is refreshing from the service. Controls remain policy-gated.</p></div><span>{task.execution?.assertions_passed ?? 0}/{task.execution?.assertions_total ?? "—"}</span></div>}
    <div className="workspace-layout">
      <div className="workspace-main">
        <section className="panel change-panel">
          <div className="panel-heading"><div><p className="eyebrow">01 · Trigger context</p><h2>What changed</h2></div><SeverityBadge severity={task.severity} /></div>
          <p className="change-summary">{task.change.summary}</p>
          <div className="operation-line"><span>{task.change.source}</span><code>{task.change.operation}</code><b>{task.change.base_version} → {task.change.target_version}</b></div>
          <div className="diff-stack">{task.change.diff.map((diff, index) => <div className="diff-row" key={`${diff.path}-${index}`}><div><small>Path</small><strong>{diff.path || "—"}</strong></div><div className="before"><small>Before</small><strong>{diff.before || "—"}</strong></div><ChevronIcon/><div className="after"><small>After</small><strong>{diff.after || "—"}</strong></div></div>)}</div>
        </section>
        <section className="panel">
          <div className="panel-heading"><div><p className="eyebrow">Context graph</p><h2>Impacted assets</h2></div><span className="panel-count">{assets.length} downstream</span></div>
          {assets.length ? <div className="asset-grid">{assets.map((asset, index) => <article key={`${asset.canonical_id}-${index}`}><span><LinkIcon /></span><div><strong>{asset.canonical_id}</strong><small>{humanize(asset.kind)} · {asset.owner}</small><p>{humanize(asset.relationship)} the changed operation · {asset.risk} risk</p></div><b>L{asset.depth}</b></article>)}</div> : <EmptyState title="No impacted assets" detail="The service returned no downstream context." />}
        </section>
        <section className="panel">
          <div className="panel-heading"><div><p className="eyebrow">02 · Proof plan</p><h2>Proof obligations</h2></div><span className="panel-count">{task.coverage.satisfied}/{task.coverage.total} satisfied</span></div>
          <div className="coverage-strip"><div><strong>{percent}%</strong><span>evidence coverage</span></div><i><b style={{ width: `${percent}%` }} /></i><span>{task.gap_count} gaps</span></div>
          <div className="obligation-list">{task.obligations.map((item) => <ObligationRow key={item.id} item={item} />)}</div>
        </section>
      </div>
      <aside className="control-column">
        <section className="proposal-card panel">
          <div className="panel-heading"><div><p className="eyebrow"><SparkIcon /> Agent proposal</p><h2>{task.proposal?.title || "No action proposed"}</h2></div>{task.proposal && <span className="proposal-source">Proposal only</span>}</div>
          {task.proposal ? <><p>{task.proposal.objective}</p><ul>{task.proposal.checks.map((check) => <li key={check}><CheckIcon />{check}</li>)}</ul>{task.proposal.explanation && <div className="agent-rationale"><strong>Agent rationale</strong><p>{task.proposal.explanation}</p></div>}<div className="boundary"><PolicyIcon /><div><strong>Execution boundary</strong><span>{task.proposal.safety_boundary}</span></div></div><div className="proposal-meta"><span>Source</span><b>{proposalSourceLabel(task.proposal.source)}</b></div></> : <p>The service has not attached an actionable proposal to this task.</p>}
        </section>
        <section className="decision-card panel">
          <div className="panel-heading"><div><p className="eyebrow">03 · Human control</p><h2>Decision gate</h2></div></div>
          {task.review ? <div className="review-record"><span><CheckIcon /></span><div><strong>{humanize(task.review.verdict)}</strong><p>{task.review.reason}</p><small>{task.review.reviewer} · {formatTime(task.review.decided_at)}</small></div></div> : <p className="decision-copy">Approval authorizes one bounded action for this exact target revision. It does not change release readiness.</p>}
          <div className="decision-facts"><div><span>Release</span><b>{humanize(task.release_decision)}</b></div><div><span>Open gaps</span><b>{task.gap_count}</b></div><div><span>Allowed actions</span><b>{task.allowed_actions.length}</b></div></div>
          {canReview && <button className="primary-action" onClick={onReview} disabled={Boolean(actionLoading)}><CheckIcon />Review proposed action</button>}
          {canExecute && <button className="run-action" onClick={onExecute} disabled={Boolean(actionLoading)}><PlayIcon />{actionLoading === "execute" ? "Starting…" : "Run approved action"}</button>}
          {!canReview && !canExecute && <div className="locked-action"><PolicyIcon /><span>Controls are locked by server policy</span></div>}
        </section>
      </aside>
    </div>
    {loading && <div className="page-loading"><span className="spinner" />Refreshing service state</div>}
  </>;
}

function ObligationRow({ item }: { item: ProofObligation }) {
  const complete = item.status === "satisfied" || item.status === "waived";
  return <article className={`obligation-row ${complete ? "obligation-complete" : ""}`}><span>{complete ? <CheckIcon /> : <AlertIcon />}</span><div><strong>{item.title}</strong><p>{item.rationale}</p><small>{humanize(item.evidence_kind)} · {item.policy_rule}</small></div><SeverityBadge severity={item.severity} /></article>;
}

function EvidencePage({ task, items, loading }: { task: TaskDetail | null; items: EvidenceView[]; loading: boolean }) {
  const valid = items.filter((item) => item.binding_status === "valid").length;
  return <><PageHeader eyebrow="Version-bound artifacts" title="Evidence Explorer" description={task ? `Evidence for ${task.title}` : "Inspect verified facts and their exact bindings."} aside={task ? <StatusBadge status={task.status} /> : undefined} />
    <div className="evidence-metrics"><article><span>Valid bindings</span><strong>{valid}</strong></article><article><span>Stale or unbound</span><strong>{items.length - valid}</strong></article><article><span>Target revision</span><strong className="revision-text">{task?.target_revision || "—"}</strong></article></div>
    <section className="panel evidence-table-panel">{loading ? <LoadingBlock lines={6}/> : items.length ? <div className="evidence-table"><div className="table-head"><span>Evidence</span><span>Binding</span><span>Outcome</span><span>Target</span><span>Collected</span></div>{items.map((item) => <article key={item.id}><div><span className={`evidence-icon binding-${item.binding_status}`}>{item.binding_status === "valid" ? <CheckIcon/> : <AlertIcon/>}</span><div><strong>{humanize(item.evidence_kind)}</strong><small>{item.module} · {item.source_run}</small></div></div><span className={`binding-pill binding-${item.binding_status}`}>{humanize(item.binding_status)}</span><span className={`outcome outcome-${item.outcome}`}>{humanize(item.outcome)}</span><code>{item.target_revision}</code><span>{formatTime(item.collected_at)}</span></article>)}</div> : <EmptyState title="No evidence returned" detail="Evidence collected for this task will appear here." />}</section>
  </>;
}

function AuditPage({ task, items, loading }: { task: TaskDetail | null; items: AuditEvent[]; loading: boolean }) {
  return <><PageHeader eyebrow="Immutable accountability" title="Audit Trail" description={task ? `Every decision and state transition for ${task.title}.` : "Append-only operations history."} aside={<div className="chain-chip"><LinkIcon />Digest chain</div>} />
    <section className="panel audit-panel">{loading ? <LoadingBlock lines={7}/> : items.length ? <div className="audit-timeline">{items.map((event) => <article key={`${event.sequence}-${event.occurred_at}`}><div className="audit-sequence"><span>{event.sequence}</span><i /></div><div className="audit-copy"><div><strong>{event.summary}</strong><time>{formatTime(event.occurred_at)}</time></div><p>{humanize(event.event_type)}</p><span>{event.actor}</span>{event.payload_sha256 && <code title={event.payload_sha256}>digest · {event.payload_sha256.slice(0, 12)}…</code>}</div></article>)}</div> : <EmptyState title="No audit events" detail="Task decisions and execution events will be recorded here." />}</section>
  </>;
}

function PoliciesPage({ items, loading }: { items: PolicyView[]; loading: boolean }) {
  return <><PageHeader eyebrow="Guardrails" title="Assurance Policies" description="The active rules that create tasks, require proof, and gate operational actions." aside={<span className="policy-count">{items.length} policies</span>} />
    {loading ? <div className="panel"><LoadingBlock lines={7}/></div> : items.length ? <div className="policy-grid">{items.map((policy) => <article className="panel policy-card" key={policy.id}><div className="policy-card-top"><span><PolicyIcon /></span><div><StatusPill value={policy.mode}/><h2>{policy.id}</h2><p>Deterministic release guardrails applied by the service.</p></div></div><div className="policy-rule-list">{policy.rules.map((rule, index) => <div key={rule}><span>{String(index + 1).padStart(2, "0")}</span><p>{rule}</p></div>)}</div><div className="policy-actions"><span>Authority</span><b>Server enforced</b><b>{humanize(policy.mode)}</b></div></article>)}</div> : <EmptyState title="No policies returned" detail="Connect the policy service to display active operational guardrails." />}
  </>;
}

function StatusPill({ value }: { value: string }) { return <span className={`generic-status generic-${value}`}>{humanize(value)}</span>; }

function ReviewDialog({ task, reason, loading, onReason, onClose, onSubmit }: { task: TaskDetail; reason: string; loading: string | null; onReason: (value: string) => void; onClose: () => void; onSubmit: (verdict: "approve" | "request_changes") => void }) {
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><section className="review-dialog" role="dialog" aria-modal="true" aria-labelledby="review-title"><header><span><PolicyIcon /></span><div><p className="eyebrow">Human authorization</p><h2 id="review-title">Review proposed action</h2></div><button onClick={onClose} aria-label="Close review">×</button></header><div className="review-scope"><div><span>Task</span><strong>{task.title}</strong></div><div><span>Target revision</span><code>{task.target_revision}</code></div><div><span>Release state</span><strong>{humanize(task.release_decision)} · {task.gap_count} gaps</strong></div></div><div className="review-warning"><AlertIcon /><p><strong>Approval changes authorization, not truth.</strong><br/>The service resolves the authenticated reviewer identity and rejects self-approval. Readiness remains blocked until fresh evidence satisfies every proof obligation.</p></div><label>Decision note<textarea value={reason} onChange={(event) => onReason(event.target.value)} maxLength={320} /></label><footer><button className="request-button" onClick={() => onSubmit("request_changes")} disabled={Boolean(loading)}>Request changes</button><button className="approve-button" onClick={() => onSubmit("approve")} disabled={Boolean(loading) || reason.trim().length < 3}>{loading === "approve" ? "Recording…" : "Approve bounded action"}</button></footer></section></div>;
}

export default App;
